import { Controller } from 'stimulus';
import Modal from 'bootstrap/js/src/modal';
import { MODAL_CLOSE_CONFIRMATION_SKIP_ONCE } from '../javascript/lib/modal_close_confirmation_attributes';

const CLASS_SCROLLABLE = 'modal-dialog-scrollable';

// The two ways a successful submission reaches this controller: the app's derived
// event (lib/turbo/submit_success), and Turbo's own end-of-submission event, which
// carries the outcome on its detail. Both mean the opener should hear about it.
const SUBMISSION_EVENTS = ['turbo:submit-success', 'turbo:submit-end'];

export default class extends Controller {
  static targets = [`remoteModal`, `frame`, 'dialog'];

  connect() {
    if (this.hasRemoteModalTarget) {
      this.modal = new Modal(this.remoteModalTarget);

      this.remoteModalTarget.addEventListener('hidden.bs.modal', () => {
        this.clearFrameContent();
        this.removeAdditionalDialogClass();
        this.resetFrameTarget();
        this.resetScroll();
      });
    }
  }

  disconnect() {
    if (this.modal) {
      // Signal that this hide is programmatic (controller disconnecting during
      // navigation) and should skip the confirmation prompt
      this.remoteModalTarget.setAttribute(
        MODAL_CLOSE_CONFIRMATION_SKIP_ONCE,
        'true'
      );
      this.modal.hide();
    }
  }

  showModal(event) {
    this.opener = event.currentTarget || event.target;

    this.removeAdditionalDialogClass();
    this.addAdditionalDialogClass(event);
    this.setFrameTarget(event);
    this.setScroll(event);
    this.modal.show(event.target);
  }

  closeModal = (event) => {
    if (!this.modal) return;

    this.modal.hide();

    // Forward the submit-success to the opener so other actions can be triggered
    if (this.opener && SUBMISSION_EVENTS.includes(event.type)) {
      const customEvent = new CustomEvent('amba:remote-modal.success', {
        bubbles: true,
        detail: { event },
      });
      this.opener.dispatchEvent(customEvent);
    }
  };

  // For a control that submits through a link rather than a form: Turbo builds a
  // form for `data-turbo-method`, appends it to the document body and removes it
  // again on submit-end, so the app's derived turbo:submit-success is dispatched on
  // a detached form and reaches nothing. Only the submit-end itself, listened for at
  // the document, arrives — and it comes for failures too, so the outcome is checked
  // here before the modal closes and the opener is told.
  closeModalOnSuccess = (event) => {
    if (!event.detail?.success) return;

    this.closeModal(event);
  };

  // There's only one remote modal on the page, so allow the triggering link
  // to pass classes to the `.modal-dialog` to adjust its appearance...
  addAdditionalDialogClass(event) {
    if (event.currentTarget.hasAttribute('data-remote-modal-dialog-class')) {
      this.additionalDialogClasses =
        event.currentTarget.dataset.remoteModalDialogClass.split(' ');
      this.dialogTarget.classList.add(...this.additionalDialogClasses);
    }
  }

  // ...and clean them up when hidden
  removeAdditionalDialogClass() {
    if (this.additionalDialogClasses?.length) {
      this.dialogTarget.classList.remove(...this.additionalDialogClasses);
      this.additionalDialogClasses = null;
    }
  }

  setFrameTarget(event) {
    const frameTarget = event.currentTarget.dataset.remoteModalTarget;

    if (frameTarget) {
      this.frameTarget.setAttribute('target', frameTarget);
    }
  }

  resetFrameTarget() {
    this.frameTarget.setAttribute('target', '_top');
  }

  setScroll(event) {
    if (event.currentTarget.dataset.remoteModalScrollable === 'false') {
      this.dialogTarget.classList.remove(CLASS_SCROLLABLE);
    }
  }

  resetScroll() {
    this.dialogTarget.classList.add(CLASS_SCROLLABLE);
  }

  // Clear the frame content while preserving the template element (used by
  // turbo-frame--loading controller for the loading spinner)
  clearFrameContent() {
    const template = this.frameTarget.querySelector('template');
    this.frameTarget.innerHTML = '';
    if (template) {
      this.frameTarget.appendChild(template);
    }
  }

  navigateAway(event) {
    event.preventDefault();
    const url = new URL(
      event.currentTarget.getAttribute('href'),
      window.location.origin
    );
    window.location.href = url.pathname;
  }
}
